import React, { Component, RefObject } from 'react';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import './AudioPlayer.css'
import play from '../../assets/player/play.webp'
import pause from '../../assets/player/pause.webp'
import moment from 'moment';
import { appendAiLabel } from '../../utils/appendAiLabel';

// 定义组件的 Props 类型
interface AudioPlayerProps {
	audioSrc: string;
	style?: React.CSSProperties; // 添加 style 属性
	autoplay?: boolean;
	isTabs?: string;
	title?: string;
	recordingType?: string;
	prohibitPlay?: boolean
}

// 定义组件的 State 类型
interface AudioPlayerState {
	isPlaying: boolean;
	isReplaying: boolean;
	currentTime: number;
	duration: number;
	playbackSrc: string;
	isPreparing: boolean;
}

class AudioPlayer extends Component<AudioPlayerProps, AudioPlayerState> {
	private audioRef: RefObject<HTMLAudioElement>;
	private labeledObjectUrl = '';
	private labelRequest = 0;

	constructor(props: AudioPlayerProps) {
		super(props);
		this.state = {
			isPlaying: false,
			isReplaying: false,
			currentTime: 0,
			duration: 0,
			playbackSrc: '',
			isPreparing: false,
		};
		this.audioRef = React.createRef();
	}

	componentDidMount() {
		const audioElement = this.audioRef.current;

		audioElement.addEventListener('timeupdate', this.handleTimeUpdate);
		audioElement.addEventListener('durationchange', this.handleDurationChange);
		audioElement.addEventListener('ended', this.handlePlaybackEnded);
		void this.prepareAudio(this.props.audioSrc);
	}

	componentWillUnmount() {
		const audioElement = this.audioRef.current;

		audioElement.removeEventListener('timeupdate', this.handleTimeUpdate);
		audioElement.removeEventListener('durationchange', this.handleDurationChange);
		audioElement.removeEventListener('ended', this.handlePlaybackEnded);
		this.labelRequest += 1;
		if (this.labeledObjectUrl) URL.revokeObjectURL(this.labeledObjectUrl);
	}
	componentDidUpdate(prevProps: AudioPlayerProps) {
		// 检测 audioSrc 属性是否发生了变化
		if (prevProps.audioSrc !== this.props.audioSrc) {
			// 重置播放状态为非播放状态
			this.audioRef.current.pause();
			this.setState({ isPlaying: false, isReplaying: false, currentTime: 0, duration: 0 });
			void this.prepareAudio(this.props.audioSrc);
		}
	}

	prepareAudio = async (audioSrc: string) => {
		const request = ++this.labelRequest;
		if (this.labeledObjectUrl) {
			URL.revokeObjectURL(this.labeledObjectUrl);
			this.labeledObjectUrl = '';
		}
		if (!audioSrc) {
			this.setState({ playbackSrc: '', isPreparing: false });
			return;
		}
		this.setState({ playbackSrc: '', isPreparing: true });
		try {
			const labeledAudio = await appendAiLabel(audioSrc);
			if (request !== this.labelRequest) return;
			this.labeledObjectUrl = URL.createObjectURL(labeledAudio);
			this.setState({ playbackSrc: this.labeledObjectUrl, isPreparing: false }, () => {
				if (this.props.autoplay) this.handlePlayPause();
			});
		} catch (error) {
			if (request !== this.labelRequest) return;
			console.error('追加 AI 标识音频失败：', error);
			this.setState({ playbackSrc: '', isPreparing: false });
		}
	};

	handlePlayPause = () => {
		if (!this.state.playbackSrc || this.state.isPreparing) return;
		const audioElement = this.audioRef.current;

		if (this.state.isReplaying) {
			audioElement.currentTime = 0;
			this.setState({ isReplaying: false });
		}
		if (this.state.isPlaying) {
			audioElement.pause();
		} else {
			audioElement.play();
		}
		this.setState({ isPlaying: !this.state.isPlaying });
	};

	handleTimeUpdate = () => {
		const audioElement = this.audioRef.current;
		// console.log('Audio current time:', audioElement.currentTime);

		this.setState({ currentTime: audioElement.currentTime });
	};

	handlePlaybackEnded = () => {
		// 播放结束时，将播放状态设置为重播状态
		this.setState({ isPlaying: false, isReplaying: true });
	};

	handleDurationChange = () => {
		const audioElement = this.audioRef.current;
		// console.log('Audio duration change:', audioElement.duration);
		this.setState({ duration: audioElement.duration || 0 });
	};

	handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
		const audioElement = this.audioRef.current;
		const newTime = Number(event.target.value);
		audioElement.currentTime = newTime;
		this.setState({ currentTime: newTime });
		if (newTime < audioElement.duration) {
			this.setState({ isReplaying: false });
		}
	};

	handleDownload = () => {
		// 如果没有传过来的值 则不下载 否则 audio会把当前连接当成地址下载
		if (!this.state.playbackSrc || this.state.isPreparing) return
		const audioElement = this.audioRef.current;
		const a = document.createElement('a');
		a.href = audioElement.src;
		const downloadName = `AI合成-${this.props.title ? this.props.title + encodeURIComponent(moment(Date.now()).format('YYYY-MM-DD HH：mm')) : Date.now()}.wav`;
		const decodedDownloadName = this.props.title ? decodeURIComponent(downloadName) : downloadName; // 解码文件名
		a.download = decodedDownloadName
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	formatTime = (time: number) => {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
	};

	render() {
		const { isReplaying, isPlaying, currentTime, duration, playbackSrc, isPreparing } = this.state;
		const { style, prohibitPlay } = this.props; // 获取传入的 style 属性
		const playButtonStyle = {
			border: "none",
			backgroundColor: isReplaying ? '#ff671d' : 'transparent',
			marginRight: 18,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		}
		const iconButtonStyle = {
			fontSize: '20px',
			color: 'white',
			marginTop: '0px'
		}
		const playbackProgressPercent = duration ? (currentTime / duration * 100).toFixed(2).toString() + "%" : "0%";
		return (
			<div className="audio-player" style={style}>
				<audio ref={this.audioRef} src={playbackSrc} />
					<div className="controls" style={{ width: '100%', minWidth: 0 }}>
						<div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
						<Button
							disabled={isPreparing || (prohibitPlay ? prohibitPlay : duration <= 0)}
							icon={
								isReplaying ?
									<ReloadOutlined rev={undefined} style={iconButtonStyle} /> :
									isPlaying ? <img src={pause} alt="" style={iconButtonStyle} /> : <img src={play} alt="" style={iconButtonStyle} />
							}
							shape="circle"
							onClick={this.handlePlayPause}
							style={playButtonStyle}>
						</Button>
						<input
							className='input-range'
							type="range"
								style={{ backgroundSize: playbackProgressPercent, height: 8, marginTop: 19, width: 'auto', flex: 1, minWidth: 0 }}
							value={currentTime.toFixed(3)}
							min={0}
							step={0.001}
							max={duration.toFixed(3)}
							onChange={this.handleSeek}
						/>
						<span className="time" style={{ marginLeft: "20px", display: 'flex', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>
							<div className='timers'>{this.formatTime(currentTime)}</div><div className='timers' style={{ margin: '0 6px' }}>/</div><div className='timers'> {this.formatTime(duration)}</div>
						</span>
						<div style={{ width: 1, height: 14, background: ' rgba(0, 0, 0, 0.1)', margin: '0 15px' }}></div>
						<div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, whiteSpace: 'nowrap' }}>
						<div style={{ color: '#666', fontSize: 12, whiteSpace: 'nowrap' }}>AI合成</div>
						<div
							style={{ cursor: 'pointer', color: '#FF671D', fontSize: 14, whiteSpace: 'nowrap' }}
							onClick={this.handleDownload}
						>
							下载至本地
						</div>
						</div>

					</div>
				</div>
			</div>
		);
	}
}

export default AudioPlayer;
